import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./TextSizeToggle.module.css";

const SIZES = ["normal", "large", "xlarge"] as const;
type TextSize = (typeof SIZES)[number];
const STORAGE_KEY = "lakseborsen:text_size";

function loadInitialSize(): TextSize {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return (SIZES as readonly string[]).includes(saved ?? "")
      ? (saved as TextSize)
      : "normal";
  } catch {
    return "normal";
  }
}

/**
 * Lets a viewer bump the whole app's base font size beyond browser zoom,
 * which most users never discover. Scales via `html[data-text-size]` in
 * tokens.css, so every rem-based size/spacing token scales with it.
 */
export default function TextSizeToggle({ compact }: { compact?: boolean }) {
  const { t } = useTranslation();
  const [size, setSize] = useState<TextSize>(loadInitialSize);

  useEffect(() => {
    document.documentElement.setAttribute("data-text-size", size);
    try {
      localStorage.setItem(STORAGE_KEY, size);
    } catch {
      // localStorage unavailable (private mode, quota) — size just won't persist
    }
  }, [size]);

  return (
    <div
      className={compact ? `${styles.group} ${styles.compact}` : styles.group}
      role="group"
      aria-label={t("textSize.label")}
    >
      {SIZES.map((s) => (
        <button
          key={s}
          type="button"
          className={
            size === s ? `${styles.btn} ${styles.btnActive}` : styles.btn
          }
          onClick={() => setSize(s)}
          aria-pressed={size === s}
          aria-label={t(`textSize.${s}`)}
        >
          <span className={styles[s]} aria-hidden="true">
            A
          </span>
        </button>
      ))}
    </div>
  );
}
